import { sidebarConfig, type SidebarItem } from '@/config/sidebar'

export interface Crumb {
  label: string
  to?: string
}

/** Root and list pages are matched exactly so e.g. /orders/new doesn't highlight "All Orders". */
export const isNavActive = (pathname: string, to: string) =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)

/** Breadcrumbs from the sidebar config: Home • Group • Page. The deepest nav match wins. */
export function breadcrumbsFor(pathname: string, title?: string): Crumb[] {
  if (pathname === '/') return []
  let best: { trail: Crumb[]; len: number } | null = null
  const visit = (items: SidebarItem[], parents: Crumb[]) => {
    for (const item of items) {
      if (item.children) visit(item.children, item.label ? [...parents, { label: item.label }] : parents)
      if (item.to && item.label && isNavActive(pathname, item.to) && (!best || item.to.length > best.len)) {
        best = { trail: [...parents, { label: item.label, to: item.to }], len: item.to.length }
      }
    }
  }
  visit(sidebarConfig, [])
  const match = best as { trail: Crumb[] } | null
  const trail: Crumb[] = [{ label: 'Home', to: '/' }, ...(match?.trail ?? [])]
  // A page below its nav entry (e.g. /products/new) gets its own title as the last crumb.
  if (title && trail.at(-1)?.to !== pathname) trail.push({ label: title })
  return trail
}

/** Icon of the sidebar section the page belongs to (Shopify shows it before the page title). */
export function navIconFor(pathname: string): SidebarItem['icon'] {
  let best: { icon: SidebarItem['icon']; len: number } | undefined
  const visit = (items: SidebarItem[], icon?: SidebarItem['icon']) => {
    for (const item of items) {
      const own = item.icon ?? icon
      if (item.children) visit(item.children, own)
      if (item.to && own && isNavActive(pathname, item.to) && (!best || item.to.length > best.len)) best = { icon: own, len: item.to.length }
    }
  }
  visit(sidebarConfig)
  return best?.icon
}

/** Editors that need the whole viewport (no max-width, no page padding). */
export const isFullBleed = (pathname: string) => pathname === '/themes'

/** Wide work surfaces: keep the page padding but drop the 1200px column. */
export const isWide = (pathname: string) =>
  /^\/campaigns\/[^/]+$/.test(pathname)

const settingsRoutes = (sidebarConfig.find((item) => item.id === 'settings')?.children ?? []).flatMap((c) => (c.to ? [c.to] : []))

/** Settings mode (§6.10): the settings group's pages, plus every `/system` page (chat sessions stays unlisted). */
export const isSettingsRoute = (pathname: string) =>
  pathname.startsWith('/system/') || settingsRoutes.some((to) => isNavActive(pathname, to))
